import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import type { AssetFolder, AssetSummary } from '../../domain';
import { useAssets, useCollection, useFavorites, useToggleFavorite } from '../../queries';
import { ApiResponseError } from '../../api/envelope';
import { AssetCard } from './AssetCard';
import { AssetPreviewModal } from './AssetPreviewModal';
import { humanizeAppType } from './assetUtils';
import { lifecycleMeta } from './lifecycleUtils';

interface Props {
  /** Which source the right pane renders. Defaults to 'folder'. */
  mode?: 'folder' | 'collection';
  /** Folder selection (folder mode). */
  folder?: AssetFolder | null;
  /** Collection selection (collection mode). */
  collectionId?: string | null;
  /** Header label for the active app (folder mode). */
  appName?: string | null;
  /** Favorites-only filter (folder mode). */
  favoritesOnly?: boolean;
  /** Lifecycle filter — single state or null for "all" (folder mode). */
  lifecycleFilter?: string | null;
}

const PAGE_SIZE = 24;

/**
 * Right-pane asset grid. In folder mode it pages assets for the selected
 * folder via `useAssets`, honoring the favorites + lifecycle filters. In
 * collection mode it renders a collection's materialized assets (sort order)
 * via `useCollection`. Favorite hearts overlay each card. Reorder UI is
 * deferred — the collection list renders read-only for now.
 */
export const AssetGrid: React.FC<Props> = ({
  mode = 'folder',
  folder = null,
  collectionId = null,
  appName = null,
  favoritesOnly = false,
  lifecycleFilter = null,
}) => {
  const isCollection = mode === 'collection';

  const [page, setPage] = useState(1);
  // Local accumulator so "Load more" appends rather than jumping pages.
  const [accumulated, setAccumulated] = useState<AssetSummary[]>([]);
  const [previewAsset, setPreviewAsset] = useState<AssetSummary | null>(null);

  // Reset paging when the folder, collection, or filters change.
  React.useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setPreviewAsset(null);
  }, [folder?.id, collectionId, favoritesOnly, lifecycleFilter, mode]);

  const folderFilter =
    !isCollection && folder
      ? {
          appAgentType: folder.appAgentType,
          // '' is the synthetic "All assets" folder: no session filter.
          sessionId: folder.sessionId || undefined,
          favorited: favoritesOnly ? true : undefined,
          lifecycleState: lifecycleFilter ?? undefined,
          page,
          pageSize: PAGE_SIZE,
        }
      : undefined;

  const assetsQuery = useAssets(folderFilter, { enabled: !isCollection && !!folder });
  const collectionQuery = useCollection(isCollection ? collectionId ?? undefined : undefined);

  // Favorited asset ids, for the heart state on each card.
  const { data: favorites } = useFavorites('Asset');
  const favoritedIds = useMemo(
    () => new Set((favorites ?? []).map(f => f.targetId)),
    [favorites],
  );
  const toggleFavorite = useToggleFavorite();
  const handleToggleFavorite = (asset: AssetSummary) =>
    toggleFavorite.mutate({ targetType: 'Asset', targetId: asset.assetId });

  // Append newly-fetched folder pages to the accumulator (folder mode only).
  React.useEffect(() => {
    if (isCollection || !assetsQuery.data?.assets) return;
    setAccumulated(prev => {
      if (page === 1) return assetsQuery.data!.assets;
      const seen = new Set(prev.map(a => a.assetId));
      const additions = assetsQuery.data!.assets.filter(a => !seen.has(a.assetId));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
  }, [assetsQuery.data, page, isCollection]);

  // Empty state — nothing selected.
  if (!isCollection && !folder) {
    return <EmptyPane message="Pick a folder on the left to see its assets." />;
  }
  if (isCollection && !collectionId) {
    return <EmptyPane message="Pick a collection on the left to see its assets." />;
  }

  const isLoading = isCollection ? collectionQuery.isLoading : assetsQuery.isLoading;
  const isFetching = isCollection ? collectionQuery.isFetching : assetsQuery.isFetching;
  const error = isCollection ? collectionQuery.error : assetsQuery.error;

  const assets = isCollection ? collectionQuery.data?.assets ?? [] : accumulated;
  const pagination = assetsQuery.data?.pagination;
  const totalCount = isCollection
    ? collectionQuery.data?.collection.assetCount ?? assets.length
    : pagination?.totalCount ?? 0;
  const hasMore = !isCollection && (pagination?.hasNextPage ?? false);

  const title = isCollection
    ? collectionQuery.data?.collection.name ?? 'Collection'
    : folder?.sessionName ?? folder?.sessionId;
  const subtitle = isCollection
    ? collectionQuery.data?.collection.description ?? 'Collection'
    : `${appName ? humanizeAppType(appName) : folder?.appAgentType}`;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        sx={{ px: 3, py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
          {totalCount > 0 && ` · ${totalCount} asset${totalCount === 1 ? '' : 's'}`}
        </Typography>
        {!isCollection && favoritesOnly && (
          <Chip label="Favorites" color="error" size="small" variant="outlined" />
        )}
        {!isCollection && lifecycleFilter && (
          <Chip
            label={lifecycleMeta(lifecycleFilter).label}
            color={lifecycleMeta(lifecycleFilter).color}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {error && (
          <Alert severity="error">
            {error instanceof ApiResponseError
              ? `${error.code}: ${error.message}`
              : error instanceof Error
                ? error.message
                : 'Failed to load assets.'}
          </Alert>
        )}

        {!isLoading && !error && assets.length === 0 && (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <ImageOutlinedIcon sx={{ fontSize: 56, opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary">
              {isCollection
                ? 'This collection is empty. Add assets from an asset preview.'
                : favoritesOnly
                  ? 'No favorited assets in this folder.'
                  : 'No assets in this folder yet.'}
            </Typography>
          </Stack>
        )}

        {assets.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 2,
            }}
          >
            {assets.map(asset => (
              <AssetCard
                key={asset.assetId}
                asset={asset}
                onClick={setPreviewAsset}
                isFavorited={favoritedIds.has(asset.assetId)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </Box>
        )}

        {hasMore && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPage(p => p + 1)}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} /> : undefined}
            >
              {isFetching ? 'Loading…' : `Load more (${totalCount - assets.length} remaining)`}
            </Button>
          </Stack>
        )}
      </Box>

      <AssetPreviewModal
        asset={previewAsset}
        assets={assets}
        favoritedIds={favoritedIds}
        onToggleFavorite={handleToggleFavorite}
        onClose={() => setPreviewAsset(null)}
        onNavigate={setPreviewAsset}
      />
    </Box>
  );
};

const EmptyPane: React.FC<{ message: string }> = ({ message }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 0,
    }}
  >
    <Stack alignItems="center" spacing={1}>
      <ImageOutlinedIcon sx={{ fontSize: 56, opacity: 0.3 }} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  </Box>
);
